import type { Event } from "@sentry/core";

/**
 * URLs whose 5xx responses `Sentry.httpClientIntegration` reports as errors.
 *
 * Only our own APIs. Excluded on purpose:
 * - `/broker/v1/events`: the SSE connection retries by itself every 2 s, so
 *   each attempt during an outage would become an event;
 * - third parties (OpenPanel analytics, Statuspage, AWS Cognito, babel
 *   translations): failures there are not actionable from this project.
 */
export const SENTRY_FAILED_REQUEST_TARGETS: RegExp[] = [
  /^https:\/\/backend(\.staging)?\.snoots\.pet\/(?!broker\/v1\/events)/,
  /^https:\/\/toolkit(\.staging)?\.snoots\.pet\//
];

const HTTP_CLIENT_MECHANISM_PREFIX = "auto.http.client";
const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_PATTERN = /^\d+$/;

export function isFailedRequestTarget(url: string): boolean {
  return SENTRY_FAILED_REQUEST_TARGETS.some((target) => target.test(url));
}

function isHttpClientEvent(event: Event): boolean {
  return (
    event.exception?.values?.some((exception) =>
      exception.mechanism?.type?.startsWith(HTTP_CLIENT_MECHANISM_PREFIX)
    ) ?? false
  );
}

function normalizeSegment(segment: string): string {
  if (
    OBJECT_ID_PATTERN.test(segment) ||
    UUID_PATTERN.test(segment) ||
    NUMERIC_PATTERN.test(segment)
  ) {
    return ":id";
  }

  return segment;
}

function normalizeEndpoint(url: string): string | null {
  let parsed: URL;

  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const path = parsed.pathname.split("/").map(normalizeSegment).join("/");

  return `${parsed.host}${path}`;
}

/**
 * Gives every failed HTTP call its own Sentry issue.
 *
 * By default Sentry groups by stack trace, which is the same Angular
 * FetchBackend/zone.js stack for every request: 5xx of unrelated endpoints end
 * up in one issue. Grouping by method + endpoint (ids normalized) + status
 * keeps each problem separate.
 */
export function applyHttpClientGrouping(event: Event): void {
  if (!isHttpClientEvent(event) || !event.request?.url) {
    return;
  }

  const endpoint = normalizeEndpoint(event.request.url);

  if (!endpoint) {
    return;
  }

  const method = (event.request.method ?? "GET").toUpperCase();
  const status = String(event.contexts?.["response"]?.["status_code"] ?? "");

  event.fingerprint = ["http-client", method, endpoint, status];
  event.tags = { ...event.tags, "http.endpoint": `${method} ${endpoint}` };
}
